import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router } from '@angular/router';
import { CourseStatusEnum } from '@edtech-ui/constants/course-status.enum';
import { QuizQuestionType } from '@edtech-ui/constants/quiz-question-type.enum';
import { ChangeQuizStatusPayload } from '@edtech-ui/core/models/quiz-review-interface.ts/quiz-status';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, of, Subscription, tap } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { GetAllQuiz, GetAllQuizQuestion, UpdateQuizNameTimePayload } from '@edtech-ui/core/models/quiz-review-interface.ts/get-all-quiz';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { QuizReorderPayload } from '@edtech-ui/core/models/quiz-review-interface.ts/quiz-reorder';
import { CustomValidators } from '@edtech-ui/core/utils/form-array-validators';
import { duplicateOptionsValidator } from '@edtech-ui/core/utils/duplicate-option-validators';
import { EditableStudyMaterialTypeEnum } from '@edtech-ui/constants/editable-material-type.enum';
import { EditCourseTitleComponent } from '@edtech-ui/dashboard/manual-course-creation/edit-course-title/edit-course-title.component';
import { UnpublishedQuizDialogBoxComponent } from '../unpublished-quiz-dialog-box/unpublished-quiz-dialog-box.component';
import { StudyMaterialTypeEnum } from '@edtech-ui/constants/material-type.enum';
import { DeleteDialogBoxComponent } from '@edtech-ui/dashboard/teacher/classroom/delete-dialog-box/delete-dialog-box.component';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { EditQuizDialogBoxComponent } from '../edit-quiz-dialog-box/edit-quiz-dialog-box.component';
import { PublishWithLinkDialogBoxComponent } from '../publish-with-link-dialog-box/publish-with-link-dialog-box.component';
import { MathLatexComponent } from '@edtech-ui/shared/latex/math-latex/math-latex.component';
import { QuestionBlocksComponent } from '@edtech-ui/shared/question-blocks/question-blocks.component';
import { getCorrectOptionIndex, isCorrectQuizOption } from '@edtech-ui/core/utils/assessment-quiz/assessment-quiz-answer.utils';
import { rebuildBlocksFromEditedText, textToBlocks } from '@edtech-ui/core/utils/question-blocks.utils';


@Component({
	selector: 'app-admin-review-edit-preview',
	standalone: true,
	imports: [CommonModule, MatProgressSpinnerModule, ReactiveFormsModule, DragDropModule, HasPermissionDirective, MathLatexComponent, QuestionBlocksComponent],
	templateUrl: './admin-review-edit-preview.component.html',
	styleUrl: './admin-review-edit-preview.component.scss'
})
export class AdminReviewEditPreviewComponent implements OnInit, OnDestroy {

	subscription = new Subscription()
	currentPopup: QuizQuestionType | null = null;
	QuizQuestionType = QuizQuestionType;
	_quizData: GetAllQuizQuestion[] | undefined;
	fullQuizData!: GetAllQuiz;
	classroomId!: number | string;
	quizId!: string;
	_quizName: string = '';
	_classroomName: string = '';
	_subjectName: string = '';
	isLoading: boolean = false;
	quizForm!: FormGroup;
	selectedQuestionId!: string | number;
	quizStatus: CourseStatusEnum = CourseStatusEnum.All;
	CourseStatusEnum = CourseStatusEnum;
	_quizTimeLimit!: number;
	EditableStudyMaterialTypeEnum = EditableStudyMaterialTypeEnum;
	isPublishing: boolean = false;
	isDraft: boolean = false;
	isNewQuiz = false;
	StudyMaterialTypeEnum = StudyMaterialTypeEnum;
	PermissionForStudents = PermissionForStudents;
	quizAttempts = '';
	quizScore = '';
	quizQuestion = '';
	courseMode = '';
	isCorrectOption = isCorrectQuizOption;
	getCorrectOptionIndex = getCorrectOptionIndex;
	editingOriginalBlocks: any[] | null = null;
	uploadedImageUrl: string | null = null;
	uploadedImagePayload: string | null = null;
	isEditingManualQuestion: boolean = false;
	isUpdating: boolean = false;


	constructor(
		private router: Router,
		public _quizService: QuizManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private dialog: MatDialog,
	) {
		this.quizForm = new FormGroup({
			question_type: new FormControl(null),
			question_text: new FormControl(null, [Validators.required]),
			options: new FormArray([], [CustomValidators.optionsValidator, duplicateOptionsValidator]),
			marks: new FormControl(1, [Validators.required, Validators.min(1)]),
			order: new FormControl(null),
			key_answer: new FormControl(null, [Validators.required]),
			correct_answer: new FormControl(null),
			questionImages: new FormArray([])
		})
	}


	ngOnInit(): void {
		this.route.paramMap.subscribe(params => {
			const classroomId = params.get('classroomId');
			const quizIdFromParams = params.get('quizId');
			if (classroomId) {
				this.classroomId = +classroomId;
			}
			if (quizIdFromParams) {
				this.quizId = quizIdFromParams;
				this.getQuizDetailsById();
			}
		})
		this._quizService.quizTimeLimit.next(this._quizTimeLimit);
		this.getCompletequizData();
	}


	getCompletequizData() {
		this.subscription.add(
			this._quizService.saveQuizReviewData.subscribe(data => {
				this._quizData = data?.questions ?? [];
			})
		);
	}


	goBack() {
		this.navigateBackOrToQuizList();
	}


	getOptionLabel(index: number): string {
		return String.fromCharCode(65 + index);
	}


	hasImageBlock(question: any): boolean {
		return question?.question_blocks?.some(
			(block: any) => block.type === 'image'
		) ?? false;
	}

	shouldRenderLegacyDiagram(question: any): boolean {
		const hasImageBlock =
			question?.question_blocks?.some(
				(b: any) => b.type === 'image'
			) ?? false;

		const hasDiagrams =
			question?.diagrams?.length > 0;

		const hasDiagramPlaceholder =
			question?.question_text?.includes('[DIAGRAM_');

		return !hasImageBlock &&
			!hasDiagrams &&
			!hasDiagramPlaceholder;
	}


	changeQuizStatus(quizStatus: CourseStatusEnum, navigateAfterChange: boolean = true) {
		const quizId = this._quizService.quizId.getValue();
		const payload: ChangeQuizStatusPayload = {
			quiz_id: quizId! || this.quizId,
			target_status: quizStatus
		}
		if (quizStatus === CourseStatusEnum.Published) this.isPublishing = true;
		else if (quizStatus === CourseStatusEnum.Draft) this.isDraft = true;

		this.subscription.add(
			this._quizService.putChangeQuizStatus(payload).pipe(
				tap(response => {
					this.isPublishing = false;
					this.isDraft = false;
					if (response.Data) {
						this._toastr.success(
							quizStatus === CourseStatusEnum.Published ?
								'Your quiz has been published and is now available to students!' :
								'Your quiz has been saved as a draft. You can continue editing anytime.');

						if (quizStatus === CourseStatusEnum.Published && this.courseMode === 'proctored_assessment') {
							this.openGenerateLinkDialogBox(navigateAfterChange);
							return;
						}
						this.clearLocalStorageAndState();
						if (navigateAfterChange) {
							this.navigateBackOrToQuizList();
						}
						this.getQuizDetailsById()
					} else {
						this._toastr.error(response.Message);
					}
				}),
				catchError(err => {
					this.isPublishing = false;
					this.isDraft = false;
					this._toastr.error('Something went wrong. Please try again.');
					return of(null);
				})
			).subscribe()
		);
	}


	getQuizDetailsById() {
		this.isLoading = true;
		if (this.quizId && this.classroomId) {
			this.subscription.add(
				this._quizService.getQuizById(this.quizId).subscribe({
					next: (response) => {
						if (response.Success && response.Data) {
							this.isLoading = false;
							this.fullQuizData = response.Data;
							this.quizStatus = this.fullQuizData.status;
							this._quizData = response.Data.questions;
							this._quizName = response.Data.quiz_name;
							this._classroomName = response.Data.classroom_name;
							this._subjectName = response.Data.subject;
							this._quizTimeLimit = response.Data.time_limit;
							this.quizAttempts = response.Data.attempts_allowed;
							this.quizScore = response.Data.passing_score;
							this.quizQuestion = response.Data.questions_per_quiz;
							this.courseMode = response.Data.mode;
						}
						else {
							this._toastr.error(response.Message);
						}
					},
					error: () => {
						this._toastr.error('Something went wrong .Please Try again')
					}
				}));
		}
	}


	deleteQuizQuestions(quizId: string, questionId: string, index: number): void {

		const getQuizId = quizId || this.quizId || this._quizService.quizId.getValue();

		if (!getQuizId || !questionId) {
			this._toastr.error('Missing quiz ID or question ID');
			return;
		}
		this.subscription.add(
			this._quizService.deleteQuizQuestion(getQuizId, questionId).subscribe({
				next: () => {
					this._toastr.success('Quiz Question deleted successfully');

					if (quizId) {
						this.getQuizDetailsById();
					}
					this._quizData = [...(this._quizData || []).filter(q => q.question_id !== questionId)];
					setTimeout(() => {
						const target =
							document.getElementById(`question-${index}`) ||
							document.getElementById(`question-${index - 1}`);
						target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
					}, 300);
				},
				error: () => {
					this._toastr.error('Failed to delete quiz question');
				}
			})
		);
	}


	openDeleteDialogBox(quizId: string, questionId: string, index: number) {
		const dialogRef = this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: { message: 'Are you sure you want to delete this question?' }
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result === true) {
				this.deleteQuizQuestions(quizId, questionId, index);
			}
		})
	}


	updateQuizQuestion() {
		this.isUpdating = true;
		const getQuizId = this.quizId || this._quizService.quizId.getValue();
		if (!getQuizId || !this.selectedQuestionId) {
			this._toastr.error('Missing quiz ID or question ID');
			this.isUpdating = false;
			return;
		}
		if (this.currentPopup === QuizQuestionType.MCQ && this.hasDuplicateOptions()) {
			this._toastr.warning('Please remove duplicate options before updating the question.');
			return;
		}
		if (!this.selectedQuestionId || !this.quizId) return;

		let payload: any;
		const editedQuestionText: string = this.quizForm.value.question_text;
		const imageUrls: string[] = this.questionImages.controls.map((c: any) => c.value);
		const rebuiltBlocks = rebuildBlocksFromEditedText(editedQuestionText, this.editingOriginalBlocks, imageUrls);

		if (this.currentPopup === QuizQuestionType.MCQ) {
			const optionsArray = this.quizForm.get('options') as FormArray;
			const correctIndex = this.correctAnswerFormControl.value;
			const correctAnswerText = correctIndex !== null ? optionsArray.at(correctIndex)?.get('text')?.value : null;
			const hasEmptyOption = optionsArray.controls.some(
				ctrl => !ctrl.get('text')?.value || ctrl.get('text')?.value.trim() === ''
			);
			if (hasEmptyOption) {
				this._toastr.warning('Options cannot be empty. Please fill all options.');
				return;
			}

			if (correctIndex === null || correctIndex === undefined) {
				this._toastr.warning('Please select a correct answer.');
				return;
			}
			payload = {
				question_type: QuizQuestionType.MCQ,
				question_text: editedQuestionText,
				question_blocks: rebuiltBlocks,
				options: this.quizForm.value.options.map((opt: any, idx: number) => ({
					text: opt.text,
					order: idx + 1,
					blocks: textToBlocks(opt.text)
				})),
				correct_answer: correctAnswerText,
				marks: this.quizForm.value.marks,
				has_diagram: this.isEditingManualQuestion ? (!!this.uploadedImagePayload || !!this.uploadedImageUrl) : undefined,
				diagram_url: this.isEditingManualQuestion ? (this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : "") : undefined,
				diagram_payload: this.isEditingManualQuestion ? (this.uploadedImagePayload || undefined) : undefined
			};
		} else if (this.currentPopup === QuizQuestionType.TRUE_FALSE) {
			payload = {
				question_type: QuizQuestionType.TRUE_FALSE,
				question_text: editedQuestionText,
				question_blocks: rebuiltBlocks,
				options: [
					{ text: 'True', order: 1, blocks: textToBlocks('True') },
					{ text: 'False', order: 2, blocks: textToBlocks('False') }
				],
				correct_answer: this.quizForm.value.correct_answer,
				marks: this.quizForm.value.marks,
				has_diagram: this.isEditingManualQuestion ? (!!this.uploadedImagePayload || !!this.uploadedImageUrl) : undefined,
				diagram_url: this.isEditingManualQuestion ? (this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : "") : undefined,
				diagram_payload: this.isEditingManualQuestion ? (this.uploadedImagePayload || undefined) : undefined
			};

		} else if (this.currentPopup === QuizQuestionType.OPEN_ENDED) {
			payload = {
				question_type: QuizQuestionType.OPEN_ENDED,
				question_text: editedQuestionText,
				question_blocks: rebuiltBlocks,
				correct_answer: this.quizForm.value.key_answer,
				marks: this.quizForm.value.marks,
				has_diagram: this.isEditingManualQuestion ? (!!this.uploadedImagePayload || !!this.uploadedImageUrl) : undefined,
				diagram_url: this.isEditingManualQuestion ? (this.uploadedImageUrl && !this.uploadedImagePayload ? this.uploadedImageUrl : "") : undefined,
				diagram_payload: this.isEditingManualQuestion ? (this.uploadedImagePayload || undefined) : undefined
			};
		}
		this.subscription.add(
			this._quizService.updateQuizQuestion(payload, getQuizId, String(this.selectedQuestionId)).pipe(
				tap(respone => {
					if (respone.Success) {
						this._toastr.success(respone.Message);
						if (this.quizId) this.getQuizDetailsById();

						const updatedQuestionId = String(this.selectedQuestionId);
						setTimeout(() => {
							const index = this._quizData?.findIndex(q => q.question_id === updatedQuestionId);
							if (index !== undefined && index > -1) {
								document.getElementById(`question-${index}`)?.scrollIntoView({
									behavior: 'smooth',
									block: 'center'
								});
							}
						}, 500);

						if (this._quizData) {
							const index = this._quizData.findIndex(q => q.question_id === String(this.selectedQuestionId));
							if (index > -1) {
								this._quizData[index] = {
									...this._quizData[index],
									...payload, question_id: String(this.selectedQuestionId),
									options: payload.options ?? []
								};
								this._quizData = [...this._quizData];
							}
						}
						this.closePopup();
						this.isUpdating = false;
					} else {
						this._toastr.error(respone.Message);
						this.isUpdating = false;
					}
				}),
				catchError(() => {
					this._toastr.error('Something went wrong while updating the quiz question');
					this.isUpdating = false;
					return of(null);
				})
			).subscribe()
		);
	}


	editQuestion(quizQuestion: any) {
		this.selectedQuestionId = quizQuestion.question_id;
		this.currentPopup = quizQuestion.question_type;
		this.editingOriginalBlocks = quizQuestion.question_blocks || null;
		this.isEditingManualQuestion = quizQuestion.is_ai === false;
		this.uploadedImageUrl = quizQuestion.diagram_url || null;
		this.quizForm.reset();

		// Populate questionImages FormArray from image blocks so user can preview and update URLs
		this.questionImages.clear();
		const imageBlocks: any[] = (quizQuestion.question_blocks || []).filter((b: any) => b.type === 'image');
		imageBlocks.forEach((block: any) => {
			this.questionImages.push(new FormControl(block.s3_key || ''));
		});

		if (quizQuestion.question_type === QuizQuestionType.MCQ) {
			this.quizForm.patchValue({
				question_text: quizQuestion.question_text,
				marks: quizQuestion.marks,
			});

			this.options.clear();
			quizQuestion.options.forEach((opt: any) => {
				this.options.push(
					new FormGroup({
						text: new FormControl(opt.text || '', Validators.required),
						order: new FormControl(opt.order || null)
					})
				);
			});
			const correctIndex = getCorrectOptionIndex(quizQuestion.options || [], quizQuestion.correct_answer);

			this.correctAnswerFormControl.setValue(correctIndex >= 0 ? correctIndex : null);

		} else if (quizQuestion.question_type === QuizQuestionType.TRUE_FALSE) {
			const correctAnswerValue = quizQuestion.correct_answer === true || quizQuestion.correct_answer === 'true' ? 'true' : 'false';
			this.quizForm.patchValue({
				question_type: QuizQuestionType.TRUE_FALSE,
				question_text: quizQuestion.question_text,
				correct_answer: correctAnswerValue,
				marks: quizQuestion.marks
			});

		} else if (quizQuestion.question_type === QuizQuestionType.OPEN_ENDED) {
			this.quizForm.patchValue({
				question_text: quizQuestion.question_text,
				key_answer: quizQuestion.correct_answer,
				marks: quizQuestion.marks
			});
		}
	}


	closePopup(event?: MouseEvent) {
		if (event) {
			event.stopPropagation();
		}

		this.quizForm.reset({
			question_type: null,
			question_text: null,
			options: [],
			correct_answer: null,
			key_answer: null,
			marks: 1,
			order: null
		});
		this.questionImages.clear();
		this.editingOriginalBlocks = null;
		this.currentPopup = null;
		this.selectedQuestionId = '';
		this.uploadedImageUrl = null;
		this.uploadedImagePayload = null;
		this.isEditingManualQuestion = false;
	}


	get options(): FormArray {
		return this.quizForm.get('options') as FormArray;
	}


	get questionImages(): FormArray {
		return this.quizForm.get('questionImages') as FormArray;
	}


	get correctAnswerFormControl(): FormControl {
		return this.quizForm.get('correct_answer') as FormControl;
	}

	onEditImageUpload(event: any) {
		const file = event.target.files[0];
		if (!file) return;

		const validTypes = ['image/jpeg', 'image/jpg', 'image/png'];
		if (!validTypes.includes(file.type)) {
			this._toastr.error('Only JPG, JPEG, and PNG formats are allowed.');
			return;
		}

		if (file.size > 5 * 1024 * 1024) {
			this._toastr.error('File size exceeds 5MB limit.');
			return;
		}

		const reader = new FileReader();
		reader.onload = (e: any) => {
			this.uploadedImageUrl = e.target.result;
			this.uploadedImagePayload = e.target.result;
		};
		reader.readAsDataURL(file);
	}

	removeImage() {
		this.uploadedImageUrl = null;
		this.uploadedImagePayload = null;
	}

	readonly MAX_OPTIONS = 4;
	readonly MIN_OPTIONS = 2;

	addOption(): void {
		if (this.options.length >= this.MAX_OPTIONS) return;
		this.options.push(
			new FormGroup({
				text: new FormControl('', Validators.required),
				order: new FormControl(this.options.length + 1)
			})
		);
	}

	removeOption(index: number): void {
		if (this.options.length <= this.MIN_OPTIONS) return;
		const correctIndex = this.correctAnswerFormControl.value;
		this.options.removeAt(index);
		if (correctIndex !== null && correctIndex !== undefined) {
			if (correctIndex === index) {
				this.correctAnswerFormControl.setValue(null);
			} else if (correctIndex > index) {
				this.correctAnswerFormControl.setValue(correctIndex - 1);
			}
		}
	}


	reorderQuizQuestions(prevIndex: number, currIndex: number): void {
		if (!this._quizData) return;

		const reordered = [...this._quizData];
		const movedItem = reordered.splice(prevIndex, 1)[0];
		reordered.splice(currIndex, 0, movedItem);

		if (!this.quizId) {
			reordered.forEach((q, index) => q.order = index + 1);

			this._quizData = reordered;
			this._quizService.saveQuizReviewData.next({
				...this.fullQuizData,
				questions: this._quizData ?? null
			} as GetAllQuiz);

			this._toastr.success('Questions reordered successfully');
		} else {
			const question_orders = reordered.map((q, index) => ({
				question_id: String(q.question_id),
				new_order: index + 1
			}));

			const option_orders: Record<string, { option_id: string, new_order: number }[]> = {};
			reordered.forEach(q => {
				if (q.options) {
					option_orders[String(q.question_id)] = q.options.map((opt, i) => ({
						option_id: String(opt.option_id),
						new_order: i + 1
					}));
				}
			});

			const payload: QuizReorderPayload = {
				question_orders,
				option_orders,
				quiz_id: this.quizId
			}

			this.subscription.add(
				this._quizService.reorderQuizQuestions(this.quizId, payload).pipe(
					tap(response => {
						if (response.Success) {
							this._toastr.success(response.Message);
							this.getQuizDetailsById();
							const movedQuestionId = reordered[currIndex].question_id;
							setTimeout(() => {
								const targetIndex = this._quizData?.findIndex(q => q.question_id === movedQuestionId);
								if (targetIndex !== undefined && targetIndex > -1) {
									document.getElementById(`question-${targetIndex}`)?.scrollIntoView({
										behavior: 'smooth', block: 'center'
									});
								}
							}, 300);
						} else {
							this._toastr.error(response.Message);
						}
					}),
					catchError(() => {
						this._toastr.error('something went wrong while reordering the quiz');
						return of(null);
					})
				).subscribe()
			);
		}
	}

	trackByQuestion(index: number, item: GetAllQuizQuestion): string {
		return String(item.question_id);
	}

	drop(event: CdkDragDrop<any>) {
		this.reorderQuizQuestions(event.previousIndex, event.currentIndex);
	}


	private hasDuplicateOptions(): boolean {
		const texts = this.options.controls.map(ctrl => (ctrl.get('text')?.value || '').trim().toLowerCase());
		const uniqueTexts = new Set(texts);
		return uniqueTexts.size !== texts.length;
	}


	get isUpdateDisabled(): boolean {
		if (!this.currentPopup) return true;

		if (this.currentPopup === QuizQuestionType.MCQ) {
			return (
				this.quizForm.get('question_text')?.invalid ||
				this.options.length < 2 ||
				this.correctAnswerFormControl.value === null ||
				this.correctAnswerFormControl.value === undefined
			);
		}

		if (this.currentPopup === QuizQuestionType.TRUE_FALSE) {
			return (
				this.quizForm.get('question_text')?.invalid ||
				this.quizForm.get('correct_answer')?.value === null
			);
		}

		if (this.currentPopup === QuizQuestionType.OPEN_ENDED) {
			return (
				this.quizForm.get('question_text')?.invalid ||
				!this.quizForm.get('key_answer')?.value
			);
		}
		return true;
	}


	deleteQuizesById(quizId: string): void {

		this.subscription.add(
			this._quizService.deleteQuizById(quizId).subscribe({
				next: () => {
					this._toastr.success('Quiz deleted successfully');
					this.router.navigate([
						'admin-student-platform',
						'admin-quiz'
					]);
				},
				error: () => {
					this._toastr.error('Failed to delete quiz');
				}
			})
		);
	}


	openDeleteQuizDialogBox(quizId: string) {
		this.dialog.open(DeleteDialogBoxComponent, {
			width: '360px',
			data: {
				title: 'Remove Quiz from Draft',
				message: 'Are you sure you want to remove this quiz from draft?',
				confirmText: 'Remove',
				cancelText: 'Cancel'
			}
		}).afterClosed().subscribe(result => {
			if (result === true) {
				this.deleteQuizesById(quizId);
			}
		});
	}


	formatTime(totalSeconds: number | null): string {
		if (!totalSeconds) return '00:00:00';

		const hours = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
		const minutes = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
		const seconds = (totalSeconds % 60).toString().padStart(2, '0');

		return `${hours}:${minutes}:${seconds}`;
	}


	postUpdateQuizNameTime(quizId: number | string, quizName: string, timeLimit?: number) {
		const storedQuizTime = Number(localStorage.getItem('time_limit')) || 60;

		const payload: UpdateQuizNameTimePayload = {
			quiz_name: quizName,
			time_limit: timeLimit ?? this._quizService.quizTimeLimit.getValue() ?? storedQuizTime,
		}
		this.subscription.add(
			this._quizService.postUpdateQuizNameTime(quizId, payload).pipe(
				tap(response => {
					if (response.Success) {
						this._toastr.success(response.Message);
						this.getQuizDetailsById();
						this._quizService.quizName.next(quizName);
					} else {
						this._toastr.error(response.Message)
					}
				}),
				catchError(() => {
					this._toastr.error('something went wrong while updating quiz name');
					return of(null);
				}),
			).subscribe()
		);
	}


	openEditTitleBox(showTimer: boolean = false) {
		if (!this.fullQuizData) return;

		const dialogRef = this.dialog.open(EditCourseTitleComponent, {
			width: '500px',
			data: {
				materialEditId: this.fullQuizData.quiz_id,
				editTitle: this.fullQuizData.quiz_name,
				label: EditableStudyMaterialTypeEnum.Quiz,
				timeLimit: this.fullQuizData.time_limit,
				showTimer
			}
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (!result) return;
			if (typeof result === 'string') {
				this.postUpdateQuizNameTime(this.fullQuizData.quiz_id, result);
			} else if (result.quiz_name || result.time_limit !== undefined) {
				this.postUpdateQuizNameTime(this.fullQuizData.quiz_id,
					result.quiz_name || this.fullQuizData.quiz_name, result.time_limit
				);
			}
		});
	}


	openUnpublishDialogBox() {
		this.dialog.open(UnpublishedQuizDialogBoxComponent, {
			width: '400px',
			data: { quizId: this.quizId }
		}).afterClosed().subscribe(result => {
			if (result === true) {
				this.changeQuizStatus(CourseStatusEnum.Draft, false);
			}
		});
	}

	goToQuizPage() {
		this.router.navigate(['/admin-student-platform/create-quiz', this.classroomId, this.quizId]);
	}


	openEditQuizDialogBox() {
		const dialogRef = this.dialog.open(EditQuizDialogBoxComponent, {
			width: '600px',
			data: {
				quizId: this.quizId
			}
		});
		dialogRef.afterClosed().subscribe((res) => {
			if (res === true) {
				this.getQuizDetailsById();
			}
		});
	}


	openGenerateLinkDialogBox(navigateAfterChange: boolean) {
		const dialogRef = this.dialog.open(PublishWithLinkDialogBoxComponent, {
			data: {
				quizId: this.quizId
			}
		});
		dialogRef.afterClosed().subscribe(result => {
			if (result === true && navigateAfterChange) {
				this.navigateBackOrToQuizList();
			}
			this.getQuizDetailsById();
		});
	}


	private navigateBackOrToQuizList() {
		const roleId = localStorage.getItem('role_id');
		const jobId = localStorage.getItem('job_id');
		if ((roleId === '8' || roleId === '14'|| roleId === 'COMPANYADMIN') && jobId) {
			this.router.navigate(['/recruiter-dashboard/jobs', jobId], { queryParams: { subTab: 'assessment' } });
		} else {
			this.router.navigate(['/admin-student-platform/admin-quiz']);
		}
	}

	private clearLocalStorageAndState() {
		const userId = localStorage.getItem('user_id');

		if (userId) {
			localStorage.removeItem(`${userId}_ai_quiz_data`);
		} else {
			localStorage.removeItem('ai_quiz_data');
		}

		this._quizService.saveQuizData.next(null);
		this._quizService.saveQuizReviewData.next(null);
	}


	removeDiagramTag(text: string | null | undefined): string {
		if (!text) return '';
		return text.replace(/\[DIAGRAM\]/gi, '').trim();
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}